import { useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import { getActiveLocaleSelector } from '@americanexpress/travel-selectors';
import { useErrorOnNotOkFetchye } from '@americanexpress/fetchye-amex';
import { ignoreHeadersByKey } from 'fetchye';
import { v4 } from 'uuid';
import { guestbookSmartFillAPIUrlSelector } from '../selectors/apiSelectors';
import locationContext from '../context/locationsContext';
import {
  GENERIC_LOCATION_KEY,
  GUESTBOOK_TYPE_MAP,
} from '../constants/categoryConstants';
import { moduleName } from '../constants';
import { useDebounce } from './useDebounce';
import { sanitizeQuery } from '../utils/sanitizeQueryUtil';

// Create reverse lookup for faster access
const typeToCategory = {};
Object.entries(GUESTBOOK_TYPE_MAP).forEach(([category, types]) => {
  types.forEach((type) => {
    typeToCategory[type] = category;
  });
});

const guestbookTypeToCategory = (locationTypes = []) => {
  // Find first type that has a mapping, default to GENERIC_LOCATION_KEY
  const mappedType = locationTypes.find((type) => typeToCategory[type]);
  return mappedType ? typeToCategory[mappedType] : GENERIC_LOCATION_KEY;
};

const guestbookDataMapper = (obj) => {
  const {
    fullName, name, locationTypes, ...other
  } = obj;

  return {
    ...other,
    id: fullName,
    name,
    label: name,
    type: guestbookTypeToCategory(locationTypes),
  };
};

export const guestbookAPIReporting = ({ key, options, error }) => ({
  moduleID: moduleName,
  component: 'useGuestbookSmartFillAPI',
  fetch: {
    url: key,
    status: error?.status,
    correlationId: options?.headers?.['Correlation-ID'],
    activeLocale: options?.headers?.Locale,
  },
});

export const useGuestbookSmartFillAPI = () => {
  const [debounce, setDebounce] = useState(true);
  const guestbookSmartFillAPIUrl = useSelector(guestbookSmartFillAPIUrlSelector);
  const activeLocale = useSelector(getActiveLocaleSelector);
  const {
    state: { location: { query: rawQuery, isDefault } },
    config: { minimumQueryLength },
  } = useContext(locationContext);
  const query = sanitizeQuery(rawQuery);

  useDebounce(setDebounce, query);

  const { isLoading, data, error } = useErrorOnNotOkFetchye(
    `${guestbookSmartFillAPIUrl}/travel/booking/v1/smartfill`,
    {
      defer: debounce || (!query || query?.length < minimumQueryLength || isDefault),
      method: 'POST',
      headers: {
        accept: '*/*',
        CLIENT_ID: 'VR-ONLINE',
        'Content-Type': 'application/json',
        CORRELATION_ID: v4(),
      },
      mapOptionsToKey: ignoreHeadersByKey(['CORRELATION_ID']),
      body: JSON.stringify({
        phrase: query,
        limit: 5,
        locale: activeLocale,
      }),
    },
    { expectedStatuses: [200], reporting: guestbookAPIReporting }
  );

  return {
    suggestions: data?.body?.locations.map((obj) => guestbookDataMapper(obj)) || [],
    isLoading,
    error: error || (data?.status !== 200),
  };
};
